import { useEffect, useState } from 'react';
import type { AiHostConfig } from './useAiConfig';

export type AiReachability = 'idle' | 'checking' | 'ok' | 'fail';

const HEALTH_TIMEOUT_MS = 8_000;

/**
 * Probes the ai_assistent backend (`GET {apiBase}/health`).
 * Returns `idle` when there is no config, `checking` while probing,
 * then `ok` or `fail`.
 */
export function useAiReachability(cfg: AiHostConfig | null): AiReachability {
    const [state, setState] = useState<AiReachability>(cfg ? 'checking' : 'idle');

    useEffect(() => {
        if (!cfg?.apiBase) {
            setState('idle');
            return;
        }

        let cancelled = false;
        const ac = new AbortController();
        const timer = window.setTimeout(() => ac.abort(), HEALTH_TIMEOUT_MS);

        setState('checking');

        const root = cfg.apiBase.replace(/\/+$/, '');
        // Apache may expose health at /health (direct proxy) or /api/health (admin UI prefix).
        const urls = [`${root}/health`, `${root}/api/health`];

        const tryFetch = (i: number): void => {
            if (cancelled || i >= urls.length) {
                if (!cancelled) setState('fail');
                return;
            }
            fetch(urls[i], { method: 'GET', signal: ac.signal, cache: 'no-store' })
                .then((resp) => {
                    if (cancelled) return;
                    if (resp.ok) {
                        setState('ok');
                        return;
                    }
                    tryFetch(i + 1);
                })
                .catch(() => tryFetch(i + 1));
        };

        tryFetch(0);

        return () => {
            cancelled = true;
            ac.abort();
            window.clearTimeout(timer);
        };
    }, [cfg?.apiBase, cfg?.token]);

    return state;
}
